import { Col, ColProps, Form, FormInstance, Input, Row, Select } from 'antd';
import { isAfter } from 'date-fns';
import { useTranslation } from 'react-i18next';
import DatePicker from '../../../../../shared/components/date-picker/date-picker';
import ClientUsersSelect from '../../../../client-users/components/client-users-select/client-users-select';
import ClientUsersUnitSelect from '../../../../client-users/components/client-users-unit-select/client-users-unit-select';
import CustomersSelect from '../../../../customers/components/customers-select/customers-select';
import { scanRequestFilterFormId } from '../../../constants/constants';
import { ScanRequestFilterFormModel } from '../../../models/scan-request-filter-form-model';
import styles from './scan-requests-header-filter-form.module.css';
import { UserSettings } from '../../../../user/models/user-settings';
import { useGetUserQuery, useGetUserSettingsQuery } from '../../../../user/api/user-api';
import { UserPermissionType } from '../../../../user/models/user-permission-type';
import { hasPermission } from '../../../../../shared/utils/has-permission';
import { parseServerDateFormat } from '../../../../../shared/utils/format-date/format-date';

interface Props {
  form: FormInstance<ScanRequestFilterFormModel>;
}

const ScanRequestsHeaderFilterForm = ({ form }: Props): JSX.Element => {
  const { t } = useTranslation('common');
  const columnsGrid: ColProps = { xs: 24, sm: 12, md: 6 };
  const { data: userSettings } = useGetUserSettingsQuery();
  const { data: currentUser } = useGetUserQuery();

  return (
    <Form id={scanRequestFilterFormId} form={form} layout="vertical">
      <Row gutter={18} align="top">
        <Col {...columnsGrid}>
          <Form.Item name="dateFromTo" label={t('signScanRequest.label.date')}>
            <DatePicker.RangePicker
              className={styles.input}
              disabledDate={date => isAfter(date, new Date())}
              format={parseServerDateFormat(userSettings?.[UserSettings.DateFormat])}
            />
          </Form.Item>
        </Col>
        <Col {...columnsGrid}>
          <Form.Item
            name="name"
            label={t('label.name')}
            rules={[
              {
                max: 255,
                message: t('errors.nameTooLong'),
              },
            ]}
          >
            <Input placeholder={t('label.name')} />
          </Form.Item>
        </Col>
        <Col {...columnsGrid}>
          <Form.Item name="user" label={t('label.user')}>
            <ClientUsersSelect placeholder={t('label.user')} />
          </Form.Item>
        </Col>
        <Col {...columnsGrid}>
          <Form.Item
            name="unit"
            rules={[
              {
                max: 255,
                message: t('signScanRequest.validationError.unitTooLong'),
              },
            ]}
            label={t('label.unit')}
          >
            <ClientUsersUnitSelect />
          </Form.Item>
        </Col>
        <Col {...columnsGrid}>
          <Form.Item name="archived" label={t('signScanRequest.label.uploadToServer')}>
            <Select
              defaultActiveFirstOption={false}
              allowClear={true}
              options={[
                {
                  value: '0',
                  label: t('signScanRequest.label.archiveOptions.onlyUnarchived'),
                },
                {
                  value: '1',
                  label: t('signScanRequest.label.archiveOptions.onlyArchived'),
                },
              ]}
            />
          </Form.Item>
        </Col>
        {currentUser && hasPermission(currentUser, UserPermissionType.ViewCustomers) && (
          <Col {...columnsGrid}>
            <Form.Item name="customer" label={t('label.customer')}>
              <CustomersSelect placeholder={t('signScanRequest.placeholder.customer')} />
            </Form.Item>
          </Col>
        )}
      </Row>
    </Form>
  );
};

export default ScanRequestsHeaderFilterForm;
